import { AxiosError, AxiosInstance } from 'axios';
import { getError } from '@/plugins/index';
import { ROLE_BLOCK_MESSAGE } from '@/utils/consts/statuses';
import { useErrorStore, useLoaderStore, useSuccessStore, useSystemStore, useUserStore } from '@/store/stores';

// Вынесено из http-axios.ts, чтобы http-axios не импортировал stores (stores импортируют http-axios).
// Подключается в main.ts до создания приложения.

interface ApiResponseError {
  errorMessage?: string;
  message?: string;
  [key: string]: any;
}

function isStringOrArray(value: any): value is string | any[] {
  return typeof value === 'string' || Array.isArray(value);
}

export function setupInterceptors(axios: AxiosInstance): void {
  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (e: AxiosError) {
      const userStore = useUserStore();
      const loaderStore = useLoaderStore();
      const systemStore = useSystemStore();
      const errorStore = useErrorStore();
      const successStore = useSuccessStore();
      if (
        e.response?.status === 401 &&
        userStore.authorized === 'authorized' &&
        !/user\/info\/$/.test(e.request.responseURL)
      ) {
        if (loaderStore.loader.show) {
          loaderStore.setLoader();
          console.log('Лоадер закрылся при 401');
        }
        systemStore.setSession();
        return Promise.reject(e);
      } else if (e.response?.status === 423) {
        //@ts-ignore
        if (e.response.data.errorMessage === ROLE_BLOCK_MESSAGE) {
          systemStore.setAccessRole();
        } else if (window.location.pathname !== '/login') {
          systemStore.setAccessUser();
        }
        return Promise.reject(e);
      }

      let errorData: ApiResponseError = {};
      const isFormDataRequest = e.config?.data instanceof FormData;
      if (isFormDataRequest && e.response?.data) {
        try {
          // Если это Blob
          if (e.response.data instanceof Blob) {
            const blobData: Blob = e.response.data;
            const contentType = e.response.headers?.['content-type'] || '';

            if (
              isStringOrArray(contentType) &&
              (contentType.includes('application/json') || contentType.includes('text/'))
            ) {
              const text = await blobData.text();
              try {
                errorData = JSON.parse(text);
              } catch {
                errorData = { message: text || 'Ошибка загрузки файла' };
              }
            } else {
              errorData = { message: 'Ошибка при обработке файла на сервере' };
            }
            e.response.data = errorData;
          }
          // Если это строка (текстовый ответ)
          else if (typeof e.response.data === 'string') {
            try {
              errorData = JSON.parse(e.response.data);
            } catch {
              errorData = { message: e.response.data || 'Ошибка загрузки файла' };
            }
            e.response.data = errorData;
          }
          // Если это уже объект, просто добавляем флаг
          else if (typeof e.response.data === 'object') {
            errorData = e.response.data;
          }
        } catch (formDataError) {
          console.error('Ошибка обработки FormData response:', formDataError);
          errorData = { message: 'Ошибка обработки ответа сервера' };
          e.response.data = errorData;
        }
      } else if (e.response?.data && e.response.config.responseType === 'blob' && e.response.data instanceof Blob) {
        const blobData: Blob = e.response.data;
        const text = await blobData.text();
        try {
          errorData = JSON.parse(text);
        } catch {
          // Пустое тело (404 у binary/<id>) или HTML от шлюза: разметку в уведомление не выводим,
          // текст ошибки возьмётся по коду статуса.
          errorData = {};
        }
        e.response.data = errorData;
      }

      if (
        e.config?.skipErrorMessage ||
        (e?.response?.status && e.config?.skipErrorMsgByStatuses?.includes(e.response.status))
      ) {
        return Promise.reject(e);
      }

      if (
        e.response?.status &&
        [409, 404, 400].includes(e.response.status) &&
        !e.config?.excludeStatuses?.includes(e.response.status)
      ) {
        // console.log(123, e);
        const message = getError(e.response);
        if (!message) {
          errorStore.setError(e);
        } else {
          successStore.addSuccess({
            message,
            color: 'error',
            manual: true,
          });
        }
      } else {
        errorStore.setError(e);
      }

      return Promise.reject(e);
    },
  );
}
